/**
 * CoverageRequestButton — "I Can't Run My Shift" button on upcoming shift cards.
 * Sprint 16: FR-COV-01
 *
 * Placed on the member's upcoming duty shift card. Triggers confirmation dialog,
 * optional reason input, and POST to coverage request API.
 *
 * Brand: dark navy #0F1B2D, gold #D4A843 accent, DCVFD red #C8322B destructive
 */

import { useState } from 'react';
import { apiFetch } from '../lib/api';

interface CoverageRequestButtonProps {
  dutyScheduleId: number;
  shiftDate: string;
  battalionName: string;
  disabled?: boolean;
  compact?: boolean;
  onRequestCreated?: (requestId: number) => void;
}

export default function CoverageRequestButton({
  dutyScheduleId,
  shiftDate,
  battalionName,
  disabled,
  compact,
  onRequestCreated,
}: CoverageRequestButtonProps) {
  const [showDialog, setShowDialog] = useState(false);
  const [reason, setReason] = useState('');
  const [positionNeeded, setPositionNeeded] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState<{ success: boolean; message: string } | null>(null);

  const handleSubmit = async () => {
    setSubmitting(true);
    try {
      const res = await apiFetch<{ id: number; status: string; message: string }>('/api/coverage/request', {
        method: 'POST',
        body: JSON.stringify({
          duty_schedule_id: dutyScheduleId,
          position_needed: positionNeeded || undefined,
          member_reason: reason || undefined,
        }),
      });
      setResult({ success: true, message: res.message });
      onRequestCreated?.(res.id);
    } catch (err) {
      setResult({ success: false, message: (err as Error).message });
    } finally {
      setSubmitting(false);
    }
  };

  if (result) {
    return (
      <div
        style={{
          padding: '12px 16px',
          borderRadius: 8,
          backgroundColor: result.success ? 'rgba(212, 168, 67, 0.12)' : 'rgba(200, 50, 43, 0.12)',
          color: result.success ? '#D4A843' : '#C8322B',
          fontSize: 14,
          textAlign: 'center',
        }}
      >
        {result.message}
      </div>
    );
  }

  return (
    <>
      <button
        type="button"
        onClick={() => setShowDialog(true)}
        disabled={disabled}
        style={{
          width: compact ? 'auto' : '100%',
          padding: compact ? '4px 10px' : '10px 16px',
          backgroundColor: 'transparent',
          border: '1px solid #C8322B',
          borderRadius: compact ? 6 : 8,
          color: '#C8322B',
          fontSize: compact ? 11 : 14,
          fontWeight: 600,
          cursor: disabled ? 'not-allowed' : 'pointer',
          opacity: disabled ? 0.5 : 1,
          transition: 'all 0.15s',
          whiteSpace: compact ? 'nowrap' : undefined,
          flexShrink: 0,
        }}
      >
        {compact ? 'Need Cover' : "I Can't Run My Shift"}
      </button>

      {showDialog && (
        <div
          style={{
            position: 'fixed',
            inset: 0,
            zIndex: 1000,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            backgroundColor: 'rgba(0, 0, 0, 0.6)',
            padding: 16,
          }}
          onClick={() => !submitting && setShowDialog(false)}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            style={{
              backgroundColor: '#0F1B2D',
              borderRadius: 12,
              padding: 24,
              maxWidth: 420,
              width: '100%',
              border: '1px solid rgba(212, 168, 67, 0.2)',
            }}
          >
            <h3
              style={{
                margin: '0 0 8px',
                color: '#D4A843',
                fontSize: 18,
                fontWeight: 700,
              }}
            >
              Request Coverage
            </h3>
            <p style={{ color: '#999', fontSize: 14, margin: '0 0 16px' }}>
              Report you're unavailable for <strong style={{ color: '#fff' }}>{shiftDate}</strong> — {battalionName}?
            </p>

            <label style={{ display: 'block', marginBottom: 12 }}>
              <span style={{ color: '#aaa', fontSize: 13, display: 'block', marginBottom: 4 }}>Reason (optional)</span>
              <textarea
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                maxLength={500}
                rows={2}
                style={{
                  width: '100%',
                  backgroundColor: 'rgba(255, 255, 255, 0.06)',
                  border: '1px solid rgba(255, 255, 255, 0.15)',
                  borderRadius: 6,
                  color: '#fff',
                  padding: '8px 10px',
                  fontSize: 14,
                  resize: 'vertical',
                }}
              />
            </label>

            <label style={{ display: 'block', marginBottom: 20 }}>
              <span style={{ color: '#aaa', fontSize: 13, display: 'block', marginBottom: 4 }}>
                Position needed (optional)
              </span>
              <select
                value={positionNeeded}
                onChange={(e) => setPositionNeeded(e.target.value)}
                style={{
                  width: '100%',
                  backgroundColor: 'rgba(255, 255, 255, 0.06)',
                  border: '1px solid rgba(255, 255, 255, 0.15)',
                  borderRadius: 6,
                  color: '#fff',
                  padding: '8px 10px',
                  fontSize: 14,
                }}
              >
                <option value="">Any position</option>
                <option value="incident_officer">Incident Officer</option>
                <option value="driver_pump_operator">Driver/Pump Operator</option>
                <option value="driver_operator">Driver Operator</option>
                <option value="aic">Attendant in Charge</option>
                <option value="aide">Aide</option>
                <option value="lead_firefighter">Lead Firefighter</option>
                <option value="firefighter">Firefighter</option>
              </select>
            </label>

            <div style={{ display: 'flex', gap: 10 }}>
              <button
                type="button"
                onClick={() => setShowDialog(false)}
                disabled={submitting}
                style={{
                  flex: 1,
                  padding: '10px 16px',
                  backgroundColor: 'transparent',
                  border: '1px solid rgba(255, 255, 255, 0.2)',
                  borderRadius: 8,
                  color: '#999',
                  fontSize: 14,
                  cursor: 'pointer',
                }}
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleSubmit}
                disabled={submitting}
                style={{
                  flex: 1,
                  padding: '10px 16px',
                  backgroundColor: '#C8322B',
                  border: 'none',
                  borderRadius: 8,
                  color: '#fff',
                  fontSize: 14,
                  fontWeight: 600,
                  cursor: submitting ? 'not-allowed' : 'pointer',
                  opacity: submitting ? 0.7 : 1,
                }}
              >
                {submitting ? 'Submitting...' : 'Confirm'}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
